/* ==========================================================================
   plan-panel.js — the product page slides into the plan, it does not reload.

   The four-step form is good and it is 1,688 lines of tested controller. This
   does not reimplement any of it: it asks Shopify for the rendered cart
   section, drops it into a sheet, and runs that section's own scripts once.

   Three rules hold this together, and all three matter:

   1. THE BUTTON IS A REAL FORM. With no JavaScript, a blocked fetch or any
      failure at all, the product form posts to /cart/add and the browser lands
      on /cart, which works today. The panel is an enhancement over a path that
      already works, never a replacement for it.

   2. THE SECTION'S SCRIPTS RUN EXACTLY ONCE. layaway-form.js binds four
      document-level listeners and returns early when #layaway-signup is
      absent, so running it twice would double-bind and could double-submit a
      contract. The panel is therefore built once per page load and only
      shown and hidden afterwards.

   3. innerHTML DOES NOT EXECUTE SCRIPTS. The injected section carries cart.js,
      the Stripe SDK, layaway-form.js and three inline blocks; each is
      re-created as a fresh element in document order, waiting for every src to
      load before the next runs, which is what `defer` would have done.
   ========================================================================== */
(function () {
  'use strict';

  var SECTION_ID = 'main-cart';
  var CART_URL = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root + 'cart'
    : '/cart';

  var panel = null;
  var built = false;
  var building = false;
  var lastFocus = null;

  function supported() {
    return !!(window.fetch && window.Promise && document.body.classList);
  }

  /* ------------------------------------------------------------ the sheet */

  function shell() {
    var el = document.createElement('div');
    el.className = 'planpanel';
    el.hidden = true;
    el.innerHTML =
      // `no-empty` is theme.css's own escape hatch from its
      // `div:empty { display: none }` reset, which otherwise deletes an
      // empty scrim and leaves the page behind the sheet undimmed and,
      // worse, un-closable by clicking away from it.
      '<div class="planpanel__scrim no-empty" data-planpanel-close></div>' +
      '<aside class="planpanel__sheet" role="dialog" aria-modal="true" aria-label="Start your plan">' +
        '<header class="planpanel__bar">' +
          '<span class="planpanel__title">Start your plan</span>' +
          '<button type="button" class="planpanel__x" data-planpanel-close aria-label="Close and keep shopping">' +
            '<span class="planpanel__xlabel">Keep shopping</span>' +
            '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
          '</button>' +
        '</header>' +
        '<div class="planpanel__body"></div>' +
      '</aside>';
    document.body.appendChild(el);

    el.addEventListener('click', function (e) {
      // e.target === el is the gap beside the sheet: still "click away".
      if (e.target === el || e.target.closest('[data-planpanel-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel && !panel.hidden) close();
    });
    return el;
  }

  /** The asset path, so ?v= cache-busters don't read as a different file. */
  function assetKey(src) {
    try { return new URL(src, location.href).pathname; } catch (e) { return src; }
  }

  var LOAD_TIMEOUT = 15000;
  var fallingBack = false;

  function fallback(err) {
    if (fallingBack) return;
    fallingBack = true;
    building = false;
    var form = panel && panel.querySelector('#layaway-signup');
    if (form) {
      form.layawayAbandoned = true;
      form.layawayReady = false;
      if (form.layawayGuard) form.layawayGuard.fail();
    }
    if (window.console) console.warn('[plan-panel] falling back to /cart', err);
    // The add may already have succeeded. Never post it again during recovery.
    window.location.assign(CART_URL);
  }

  function waitFor(check) {
    return new Promise(function (resolve, reject) {
      var started = Date.now();
      function poll() {
        if (check()) return resolve();
        if (Date.now() - started >= LOAD_TIMEOUT) return reject(new Error('Checkout initialization timed out'));
        setTimeout(poll, 50);
      }
      poll();
    });
  }

  /** A tag's presence does not prove that its code loaded or initialized. */
  function scriptReady(src, form) {
    var key = assetKey(src);
    if (/\/layby-plan\.js$/.test(key)) return !!(window.LaybyPlan && typeof window.LaybyPlan.laybyPlan === 'function');
    if (/\/layaway-region\.js$/.test(key)) return !!(window.LAYAWAY_REGION && form && window.LAYAWAY_REGION.code === form.dataset.layawayRegion);
    if (/\/cart\.js$/.test(key)) return !!customElements.get('main-cart');
    if (/\/gift-wrapping\.js$/.test(key)) return !!customElements.get('gift-wrap-remove-item');
    if (/\/layaway-form\.js$/.test(key)) return !!(form && form.layawayReady);
    if (/js\.stripe\.com/.test(src)) return !!window.Stripe;
    return false;
  }

  function runScripts(root) {
    var form = root.querySelector('#layaway-signup');
    if (!form) return Promise.reject(new Error('Checkout form is missing'));
    var nodes = Array.prototype.slice.call(root.querySelectorAll('script'));
    return nodes.reduce(function (chain, old) {
      return chain.then(function () {
        if (fallingBack) throw new Error('Checkout initialization abandoned');
        // Plan/configuration JSON stays in the form for its controller to read.
        if (old.type === 'application/json') return;
        if (old.src && scriptReady(old.src, form)) { old.remove(); return; }
        var optional = old.hasAttribute('data-checkout-optional');
        var existing = old.src && Array.prototype.find.call(document.scripts, function (other) {
          return other !== old && !root.contains(other) && other.src && assetKey(other.src) === assetKey(old.src);
        });
        if (existing && !optional) {
          // Never re-execute a class declaration or a partially initialized controller.
          return waitFor(function () { return scriptReady(old.src, form); }).then(function () { old.remove(); });
        }
        if (existing && optional) { old.remove(); return; }
        var s = document.createElement('script');
        Array.prototype.forEach.call(old.attributes, function (a) {
          if (a.name !== 'defer' && a.name !== 'async') s.setAttribute(a.name, a.value);
        });
        if (!old.src) {
          s.textContent = old.textContent;
          old.replaceWith(s);
          return;
        }
        if (optional) {
          // Maps and Stripe must not hold up manual address entry. The payment
          // controller handles Stripe availability and exposes a retry on failure.
          s.async = true;
          old.replaceWith(s);
          return;
        }
        return new Promise(function (resolve, reject) {
          function finish(error) {
            clearTimeout(timer);
            s.onload = s.onerror = null;
            if (error) { s.remove(); reject(error); } else resolve();
          }
          var timer = setTimeout(function () { finish(new Error('Checkout script timed out: ' + assetKey(s.src))); }, LOAD_TIMEOUT);
          s.onload = function () {
            var key = assetKey(s.src);
            var needsRegistration = /\/(cart|gift-wrapping|layby-plan|layaway-region)\.js$/.test(key);
            finish(needsRegistration && !scriptReady(s.src, form) ? new Error('Checkout dependency did not initialize: ' + key) : null);
          };
          s.onerror = function () { finish(new Error('Checkout script failed: ' + assetKey(s.src))); };
          old.replaceWith(s);
        });
      });
    }, Promise.resolve()).then(function () {
      return waitFor(function () {
        return form.layawayReady && window.LayawayForm && window.LayawayForm.form === form;
      });
    });
  }

  /**
   * Add to cart AND render the cart section in ONE request.
   *
   * /cart/add.js takes a `sections` field and returns those sections rendered,
   * so this used to be two sequential round trips — POST the add, then GET
   * /cart?sections= — with nothing on screen for the length of both. On a
   * phone that read as a dead button and people tapped it again.
   *
   * If the response comes back without the section (an older Shopify, a proxy
   * that strips the field), fall back to fetching it separately rather than
   * failing: one slow path still beats landing on /cart.
   */
  function addAndRender(form) {
    var data = new FormData(form);
    data.append('sections', SECTION_ID);
    data.append('sections_url', CART_URL);
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: data
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    }).then(function (json) {
      added[variantOf(form)] = true;
      // The header bubble stayed at 0 after this add until something else
      // refreshed it. Tell the theme, without waiting on it — the sheet must
      // not be a round trip slower for a badge behind it.
      fetch(CART_URL + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart }); })
        .catch(function () {});
      var html = json && json.sections && json.sections[SECTION_ID];
      return html || fetchSection();
    });
  }

  var added = {};

  /**
   * `added` says which variants this page has already put in the cart, so a
   * second Start opens the sheet instead of adding again. The cart can change
   * underneath it — a remove inside the sheet, an edit in the header drawer —
   * so it is rebuilt from the real cart on every cart:updated.
   */
  document.addEventListener('cart:updated', function (e) {
    var cart = e && e.detail && e.detail.cart;
    if (!cart || !cart.items) return;
    added = {};
    cart.items.forEach(function (it) { added[String(it.variant_id || it.id)] = true; });
  });

  function variantOf(form) {
    var el = form.querySelector('[name="id"]');
    return el ? String(el.value) : 'default';
  }

  /** Add to cart and stay put, so someone can keep shopping. */
  function addOnly(form) {
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: new FormData(form)
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    }).then(function () {
      added[variantOf(form)] = true;
      return refreshCart();
    });
  }

  /**
   * Tell the theme the cart changed, so the header count and bubble follow.
   * FoxTheme's pubsub is the theme's own channel; if it is not there we have
   * simply added an item quietly, which is not worth failing the flow over.
   */
  /**
   * Refresh the cart after an add of our own, without going through the
   * theme's re-render handlers.
   *
   * The theme's pubsub calls subscribers in order and stops at the first
   * that throws. Its handlers expect a quantity event of their own — a line
   * number, a control name — and on any other publish that carries sections
   * the header drawer's handler falls through to its focus trap and throws
   * on a null, so nothing after it runs. Bundling the drawer's section made
   * no difference. So:
   *
   *   1. the cart object alone goes to the pubsub — that is what moves the
   *      header count, and without `sections` every handler returns early;
   *   2. the sheet's items block is swapped by hand from a fresh render,
   *      the same six lines cart.js would have run, and `.is-empty` follows;
   *   3. cart:updated is dispatched, which is what layaway-form.js listens
   *      for to re-plan and what rebuilds `added`.
   */
  function refreshCart() {
    return Promise.all([
      fetch(CART_URL + '.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); }),
      fetch(CART_URL + '?sections=' + SECTION_ID).then(function (r) { return r.ok ? r.json() : {}; }).catch(function () { return {}; })
    ]).then(function (both) {
      var cart = both[0];
      try {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
      } catch (e) {
        if (window.console) console.warn('[plan-panel] header count not refreshed', e);
      }
      swapItems(both[1] && both[1][SECTION_ID], cart);
      try {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      } catch (e) {}
      return cart;
    }).catch(function () { return null; });
  }

  function swapItems(html, cart) {
    if (!panel || !html) return;
    var mainCart = panel.querySelector('#MainCart-' + SECTION_ID);
    if (!mainCart) return;
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#MainCart-' + SECTION_ID);
    if (!fresh) return;
    mainCart.innerHTML = fresh.innerHTML;
    var wrap = mainCart.closest('.cart');
    if (wrap) wrap.classList.toggle('is-empty', !cart || !cart.item_count);
  }

  /* ------------------------------------------------------------- the toast */

  var toastEl = null;
  var toastTimer = null;

  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'plantoast no-empty';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML =
      '<span class="plantoast__tick" aria-hidden="true"></span>' +
      '<span class="plantoast__msg"></span>' +
      '<button type="button" class="plantoast__go">Start your plan</button>';
    toastEl.querySelector('.plantoast__msg').textContent = message;
    toastEl.querySelector('.plantoast__go').addEventListener('click', function () {
      hideToast();
      var b = document.querySelector('[data-plan-start]');
      if (b) b.click();
    });
    requestAnimationFrame(function () { toastEl.classList.add('is-up'); });
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 6000);
  }

  function hideToast() {
    if (toastEl) toastEl.classList.remove('is-up');
  }

  function fetchSection() {
    // No Accept: application/json here. The /cart route answers that header
    // with the CART OBJECT and ignores ?sections= entirely, so asking politely
    // for JSON is exactly what stops the Section Rendering API working.
    return fetch(CART_URL + '?sections=' + SECTION_ID)
      .then(function (r) {
        if (!r.ok) throw new Error('section failed');
        return r.json();
      })
      .then(function (json) {
        var html = json[SECTION_ID];
        if (!html) throw new Error('section empty');
        return html;
      });
  }

  var coarse = function () {
    return window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  };

  /**
   * The first thing to fill in, not the first thing in the DOM. The DOM's
   * first input is the cart row's quantity box, so focus used to land there
   * and a customer who started typing changed their quantity. On a phone the
   * sheet is not focused at all: a raised keyboard shoves a bottom sheet
   * around before anyone has read what it says.
   */
  function focusFirst() {
    if (!panel || panel.hidden || coarse()) return;
    var first = panel.querySelector('#layaway-signup input:not([type="hidden"]):not([disabled]), #layaway-signup select');
    if (!first) return;
    setTimeout(function () {
      // This runs once the checkout controller is ready. If
      // the customer has already clicked into a field by then, that field
      // is theirs; stealing it mid-word is worse than no autofocus at all.
      if (panel.hidden || !panel.classList.contains('is-open') || fallingBack) return;
      var a = document.activeElement;
      if (a && a !== document.body && panel.contains(a)) return;
      try { first.focus({ preventScroll: true }); } catch (e) {}
    }, 120);
  }

  /**
   * Everything behind the sheet is inert while it is up, so Tab cannot wander
   * into the page and a screen reader is not read a product page it cannot
   * see. The toast is a sibling of the panel and stays live.
   */
  function setInert(on) {
    Array.prototype.forEach.call(document.body.children, function (node) {
      if (node === panel || node === toastEl) return;
      if (on) node.setAttribute('inert', '');
      else node.removeAttribute('inert');
    });
  }

  /**
   * overflow:hidden on body is enough on a desktop. iOS ignores it once a
   * finger drags at the sheet's edge, so a phone pins the body where it was
   * and puts it back on close — the classic lock, done only where needed so
   * a desktop never sees its scrollbar blink.
   */
  var lockY = 0;
  function lockPage() {
    lockY = window.pageYOffset || 0;
    document.body.classList.add('planpanel-open');
    if (coarse()) {
      document.body.style.top = -lockY + 'px';
      document.body.classList.add('planpanel-pinned');
    }
  }
  function unlockPage() {
    var pinned = document.body.classList.contains('planpanel-pinned');
    document.body.classList.remove('planpanel-open', 'planpanel-pinned');
    document.body.style.top = '';
    if (pinned) window.scrollTo(0, lockY);
  }

  /**
   * Back closes the sheet. Without this, back with the sheet up leaves the
   * site entirely — on a phone, where back is the way out of everything, the
   * checkout was one thumb-press from about:blank.
   *
   * open() pushes a state. A visual close pops it, and the popstate that
   * pop causes finds nothing left to do; a hardware back pops it first and
   * the popstate does the visual close instead. `pushed` is what tells the
   * two apart.
   */
  var pushed = false;

  function open() {
    if (!panel) return;
    if (!panel.hidden) return;
    lastFocus = document.activeElement;
    panel.hidden = false;
    lockPage();
    setInert(true);
    // a frame between display and transform, or the slide never animates
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { panel.classList.add('is-open'); });
    });
    if (window.history && history.pushState) {
      try { history.pushState({ planpanel: true }, ''); pushed = true; } catch (e) { pushed = false; }
    }
    if (built) focusFirst();
  }

  function hidePanel() {
    if (!panel || panel.hidden) return;
    panel.classList.remove('is-open');
    unlockPage();
    setInert(false);
    var done = function () { panel.hidden = true; };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else setTimeout(done, 340);
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (e) {} }
  }

  function close() {
    hidePanel();
    if (pushed) {
      pushed = false;
      try { history.back(); } catch (e) {}
    }
  }

  window.addEventListener('popstate', function () {
    if (!pushed) return;
    pushed = false;
    hidePanel();
  });

  /* ------------------------------------------------------------ the hook */

  var SKELETON =
    '<div class="planpanel__load" role="status" aria-live="polite">' +
      '<span class="planpanel__spin" aria-hidden="true"></span>' +
      '<p class="planpanel__loadtxt">Setting up your plan\u2026</p>' +
    '</div>';

  /**
   * The sheet opens on the tap, not on the response.
   *
   * It used to wait for an add, a section render and every injected script
   * before it moved, so the button sat there doing nothing for seconds. Now
   * the slide starts immediately over a skeleton and the real form replaces
   * it when it arrives — which is also why a second tap can no longer look
   * like the first one failed.
   */
  function start(form, button) {
    if (building || fallingBack) return;
    if (built) {
      if (added[variantOf(form)]) { open(); return; }
      // Removed inside the sheet, then Start again: put it back, then show it.
      if (button) button.setAttribute('aria-busy', 'true');
      addOnly(form)
        .then(function () { if (button) button.removeAttribute('aria-busy'); open(); })
        .catch(function (err) {
          if (window.console) console.warn('[plan-panel] re-add failed, falling back to /cart', err);
          fallback(err);
        });
      return;
    }
    building = true;
    if (button) button.setAttribute('aria-busy', 'true');

    panel = panel || shell();
    var body = panel.querySelector('.planpanel__body');
    body.innerHTML = SKELETON;
    open();

    var work = added[variantOf(form)] ? fetchSection() : addAndRender(form);
    var workTimer;
    new Promise(function (resolve, reject) {
      workTimer = setTimeout(function () { reject(new Error('Cart section timed out')); }, LOAD_TIMEOUT);
      work.then(resolve, reject);
    })
      .then(function (html) {
        clearTimeout(workTimer);
        if (fallingBack) throw new Error('Checkout initialization abandoned');
        body.innerHTML = html;
        return runScripts(body);
      })
      .then(function () {
        if (fallingBack) return;
        built = true;
        building = false;
        if (button) button.removeAttribute('aria-busy');
        body.scrollTop = 0;
        focusFirst();
      })
      .catch(function (err) {
        clearTimeout(workTimer);
        fallback(err);
      });
  }

  var pressed = null;

  function formFor(button) {
    // `button.form` resolves a form="..." attribute, which closest() cannot.
    return button.form || button.closest('form');
  }

  function bindButton(button) {
    if (button.dataset.planBound) return;
    var form = formFor(button);
    if (!form) return;
    button.dataset.planBound = '1';

    button.addEventListener('click', function () {
      pressed = button;
      form.dataset.planIntent = button.hasAttribute('data-plan-add') ? 'add' : 'start';
    });

    if (form.dataset.planFormBound) return;
    form.dataset.planFormBound = '1';
    form.addEventListener('submit', function (e) {
      if (!supported()) return;      // let the form post the old way
      e.preventDefault();
      var button = pressed && formFor(pressed) === form ? pressed : null;
      if (form.dataset.planIntent === 'add') {
        if (button) button.setAttribute('aria-busy', 'true');
        addOnly(form)
          .then(function () {
            if (button) button.removeAttribute('aria-busy');
            toast('Added to your cart.');
          })
          .catch(function (err) {
            if (window.console) console.warn('[plan-panel] add failed', err);
            if (button) button.removeAttribute('aria-busy');
            fallback(err);
          });
        return;
      }
      start(form, button);
    });
  }

  function bind() {
    document.querySelectorAll('[data-plan-start], [data-plan-add]').forEach(bindButton);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
  document.addEventListener('shopify:section:load', bind);

  window.LaybyPlanPanel = { open: open, close: close };
})();
